import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { SharedActions } from './shared.actions';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthApiService } from '../services/api/auth.api-service';
import { AuthenticationService } from '../services/authentication.service';
import { CurrentUserService } from '../services/current-user/current-user.service';
import { LocaleService } from '~shared/services/locale.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PuToastrService } from '~shared/services/toastr/pu-toastr.service';
import { ModulesService } from '~shared/services/modules/modules.service';
import { mapMeModules } from '~shared/utils/me-modules.mapper';
import { PU_ERROR_CODE_HEADER } from '~shared/models/general/sso.model';

@Injectable()
export class SharedEffects {
  private actions$ = inject(Actions);
  private authApiService = inject(AuthApiService);
  private authenticationService = inject(AuthenticationService);
  private currentUserService = inject(CurrentUserService);
  private modulesService = inject(ModulesService);
  private toastr = inject(PuToastrService);
  private translate = inject(TranslateService);
  private localeService = inject(LocaleService);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.login),
      switchMap(({ email, password, remember }) =>
        this.authApiService.login(email, password).pipe(
          map(({ token }) => SharedActions.loginSuccess({ token, remember })),
          catchError((error: HttpErrorResponse) => {
            this.toastr.showErrorMessage(error);
            return of(
              SharedActions.loginFailure({
                errorCode:
                  error?.headers?.get(PU_ERROR_CODE_HEADER) ??
                  error?.error?.error,
              })
            );
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedActions.loginSuccess),
        tap(({ token, remember }) => {
          this.authenticationService.saveJwtToken(token, remember);
        }),
        switchMap(() =>
          this.currentUserService.load().pipe(
            tap((me) => {
              this.modulesService.setModules(mapMeModules(me));
              // Le token etant desormais present, recharge le catalogue i18n : le
              // loader ajoute GET /me/translation (private + overrides du compte) a
              // la base publique deja chargee.
              this.translate
                .reloadLang(this.localeService.getLanguage())
                .subscribe();
            }),
            catchError(() => of(null))
          )
        )
      ),
    { dispatch: false }
  );
}
